import React, { useEffect, useState } from 'react';
import { GlobalProviders } from '@app/core/GlobalProviders';
import { RoutingLayer } from '@app/core/routing/RoutingLayer';
import '@app/core/initialize-services/InitializeServices';
import 'react-responsive-modal/styles.css';
import './i18n/config';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import { SkipLink } from '@app/shared/components/accessibility/SkipLink';
import { GlobalLiveAnnouncer } from '@app/shared/components/accessibility/LiveAnnouncer';
import { OnlineStatusAnnouncer } from '@app/shared/components/accessibility/OnlineStatusAnnouncer';
import { AccessibilityControls } from '@app/shared/components/accessibility/AccessibilityControls';
import '@app/shared/components/accessibility/accessibility-styles.css';
import { renderStore, RenderDebugPanel } from '@app/shared/debug';

function App() {
	const [isNotSupportedBrowser, setIsNotSupportedBrowser] = useState(false);
	const { t, i18n } = useTranslation();

	// Apply saved accessibility zoom on startup
	useEffect(() => {
		try {
			const stored = localStorage.getItem('dguide-accessibility-settings');
			if (stored) {
				const parsed = JSON.parse(stored);
				const multipliers: Record<string, number> = { small: 1, medium: 1.15, large: 1.3, 'x-large': 1.45 };
				const multiplier = multipliers[parsed.fontSize] || 1;
				const appEl = document.querySelector('.App') as HTMLElement | null;
				if (appEl && multiplier !== 1) {
					(appEl.style as any).zoom = multiplier.toString();
				}
			}
		} catch (e) { /* ignore */ }
	}, []);

	useEffect(() => {
		const userAgent = navigator.userAgent.toLowerCase();
		const isChromeBased = /chrome|chromium|crios|edg|opera/.test(userAgent);
		const isSafari = /safari/.test(userAgent) && !/chrome|chromium|crios|edg|opera/.test(userAgent);
		const isFirefox = /firefox/.test(userAgent);

		if (!isChromeBased && !isSafari && !isFirefox) {
			setIsNotSupportedBrowser(true);
		}

		// Exit-app popstate guard is now in RoutingLayer.tsx (module scope,
		// registered before React Router so it fires first).
	}, []);

	// Update HTML lang and dir attributes based on language
	useEffect(() => {
		const lang = (i18n as any).language || 'he';
		const dir = lang === 'he' ? 'rtl' : 'ltr';
		document.documentElement.lang = lang;
		document.documentElement.dir = dir;
	}, [i18n.language]);

	return (
		<>
			<SkipLink />
			<GlobalLiveAnnouncer />
			<OnlineStatusAnnouncer />
			{isNotSupportedBrowser ? (
				<main id="main-content" role="main" style={{ margin: 24, paddingTop: '50%' }}>
					<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						<Typography variant="h4" component="h1">{t('on_tour.not_supported_browser')}</Typography>
					</div>
					<div style={{ display: 'flex', textAlign: 'center', justifyContent: 'center', alignItems: 'center' }}>
						<Typography variant="body1">{t('on_tour.not_supported_browser_description')}</Typography>
					</div>
				</main>
			) : (
				<div className="App">
					<GlobalProviders>
						{/* <AccessibilityControls position="top-right" /> */}
						<RoutingLayer />
					</GlobalProviders>
				</div>
			)}
			{renderStore.isEnabled() && <RenderDebugPanel />}
		</>
	);
}
export default App;